import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, Box, Typography, Stack, IconButton, Slide, Avatar, Collapse, CircularProgress, Grid, useTheme, useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  AssignmentLate, TrendingUp, AccountBalance, Sell,
  ShoppingCart, Store, ContentCut, Inventory2,
  WarningAmber, AccessTime, ChevronRight,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

/* Helper: Format date to DD.MM.YYYY */
const fmtDate = (d) => {
  if (!d) return '–';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return `${String(dt.getDate()).padStart(2, '0')}.${String(dt.getMonth() + 1).padStart(2, '0')}.${dt.getFullYear()}`;
  } catch { return d; }
};

/* Helper: Format currency */
const fmtCur = (n) => (n || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const NotificationPopup = ({ open, onClose, initialExpandedLabel }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setExpandedIdx(null);
    fetch(`${API_BASE_URL}/api/notifications/summary`)
      .then(r => r.json())
      .then(d => { 
        setData(d); 
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [open]);

  // Build notification categories from data
  const items = useMemo(() => {
    const arr = [];
    if (!data) return arr;

    if (data.overdueTasks > 0) arr.push({
      icon: <AssignmentLate />, label: 'Überfällige Aufgaben',
      count: data.overdueTasks,
      color: '#ef4444', bg: '#fef2f2', border: '#fecaca',
      route: '/kanban-aufgaben/kanban', priority: 2,
      detailItems: (data.overdueTaskItems || [])
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
        .map(t => ({
          title: t.title,
          subtitle: `Fällig: ${fmtDate(t.dueDate)} · ${t.prioritaet || t.status || ''}`,
          route: '/kanban-aufgaben/kanban',
          state: { filterId: t._id || t.id }
        }))
    });
    if (data.openOrders > 0) arr.push({
      icon: <ShoppingCart />, label: 'Offene Aufträge',
      count: data.openOrders,
      color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe',
      route: '/crm-sales/auftraege', priority: 2,
      detailItems: (data.openOrderItems || []).map(o => ({
        title: o.kunde || `Auftrag #${o.auftragsNr}`,
        subtitle: `${o.auftragsNr ? `Nr. ${o.auftragsNr} · ` : ''}${fmtCur(o.betrag)}${o.totalQty ? ` · ${o.totalQty} Stk.` : ''} · ${o.status || ''} (${o.source || 'Intern'})`,
        route: '/crm-sales/auftraege',
        state: { filterId: o.auftragsNr || o._id }
      }))
    });
    if (data.datevIncomes > 0) arr.push({
      icon: <TrendingUp />, label: 'Einnahmen → DATEV',
      count: data.datevIncomes,
      color: '#10b981', bg: '#ecfdf5', border: '#bbf7d0',
      route: '/finanzen-controlling/datev?tab=incomes', priority: 3,
      detailItems: (data.datevIncomeItems || [])
        .sort((a, b) => new Date(a.datum) - new Date(b.datum))
        .map(i => ({
          title: i.kunde || 'Unbekannt',
          subtitle: `${fmtDate(i.datum)} · ${fmtCur(i.betrag)}${i.kundenkategorie ? ` · ${i.kundenkategorie}` : ''}`,
          route: '/finanzen-controlling/datev?tab=incomes',
          state: { filterId: i.rechnungsdatum || i._id }
        }))
    });
    if (data.datevExpenses > 0) arr.push({
      icon: <AccountBalance />, label: 'Ausgaben → DATEV',
      count: data.datevExpenses,
      color: '#ef4444', bg: '#fef2f2', border: '#fecaca',
      route: '/finanzen-controlling/datev?tab=expenses', priority: 3,
      detailItems: (data.datevExpenseItems || [])
        .sort((a, b) => new Date(a.datum) - new Date(b.datum))
        .map(e => ({
          title: e.kaufort || 'Unbekannt',
          subtitle: `${fmtDate(e.datum)} · ${fmtCur(e.betrag)}${e.kategorie ? ` · ${e.kategorie}` : ''}`,
          route: '/finanzen-controlling/datev?tab=expenses',
          state: { filterId: e.datum || e._id }
        }))
    });
    if (data.schnittfaellig > 0) arr.push({
      icon: <ContentCut />, label: 'Schnittbereit',
      count: data.schnittfaellig,
      color: '#f59e0b', bg: '#fffbeb', border: '#fde68a',
      route: '/produktion-logistik/verpackung', priority: 4,
      detailItems: (data.schnittfaelligItems || []).map(p => ({
        title: p.charge || p.name || 'Charge',
        subtitle: `${p.name ? `${p.name} · ` : ''}${p.daysRemaining} Tage fällig`,
        route: `/produktion-logistik/verpackung?charge=${encodeURIComponent(p.charge)}`
      }))
    });
    if (data.verpackungsbereit > 0) arr.push({
      icon: <Inventory2 />, label: 'Verpackungsbereit',
      count: data.verpackungsbereit,
      color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe',
      route: '/produktion-logistik/verpackung', priority: 1,
      detailItems: (data.verpackungItems || [])
        .sort((a, b) => new Date(a.datum) - new Date(b.datum))
        .map(v => ({
          title: v.charge || v.name || 'Charge',
          subtitle: `${v.name ? `${v.name} · ` : ''}${fmtDate(v.datum)}`,
          route: `/produktion-logistik/verpackung?charge=${encodeURIComponent(v.charge)}`
        }))
    });
    if (data.missingPriceRules > 0) arr.push({
      icon: <Sell />, label: 'Fehlende Preise',
      count: data.missingPriceRules,
      color: '#f97316', bg: '#fff7ed', border: '#fed7aa',
      route: '/sortiment/preise', priority: 5,
      detailItems: (data.missingPriceRuleItems || []).map(c => ({
        title: c.name || 'Kunde',
        subtitle: c.ort ? `Standort: ${c.ort}` : 'Keine Preisregel zugeordnet',
        route: '/sortiment/preise'
      }))
    });
    if (data.standgebuehrenFaellig > 0) arr.push({
      icon: <Store />, label: 'Standgebühren offen',
      count: data.standgebuehrenFaellig,
      color: '#f97316', bg: '#fff7ed', border: '#fed7aa',
      route: '/crm-sales/maerkte', priority: 5,
      detailItems: (data.standgebuehrenItems || [])
        .sort((a, b) => new Date(a.datum) - new Date(b.datum))
        .map(m => ({
          title: m.name || 'Markt',
          subtitle: `${m.ort ? `${m.ort} · ` : ''}${fmtDate(m.datum)}`,
          route: '/crm-sales/maerkte'
        }))
    });
    if (data.lowStockCount > 0) arr.push({
      icon: <WarningAmber />, label: 'Produkte unter Schwellenwert',
      count: data.lowStockCount,
      color: '#eab308', bg: '#fefce8', border: '#fef08a',
      route: '/produktion-logistik/lagerbestand', priority: 6,
      detailItems: (data.lowStock || []).map(p => ({
        title: p.name || 'Produkt',
        subtitle: `Bestand: ${p.lagerbestand} / Schwelle: ${p.schwellenwert}`,
        route: '/produktion-logistik/lagerbestand'
      }))
    });
    if (data.missingWorkDays > 0) arr.push({
      icon: <AccessTime />, label: 'Ohne Zeiterfassung',
      count: data.missingWorkDays,
      color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe',
      route: '/produktion-logistik/arbeitszeiten', priority: 7,
      detailItems: (data.missingWorkDaysList || [])
        .sort((a, b) => new Date(a) - new Date(b))
        .map(d => ({
          title: fmtDate(d),
          subtitle: 'Keine Zeiterfassung vorhanden',
          route: '/produktion-logistik/arbeitszeiten',
          state: { filterId: d }
        }))
    });
    if (data.incompleteEmployeesCount > 0) arr.push({
      icon: <PersonIcon />, label: 'Unvollständige MA',
      count: data.incompleteEmployeesCount,
      color: '#ef4444', bg: '#fef2f2', border: '#fecaca',
      route: '/verwaltung/mitarbeiter', priority: 8,
      detailItems: (data.incompleteEmployees || []).map(e => ({
        title: e.name,
        subtitle: `Es fehlt: ${e.missing}`,
        route: '/verwaltung/mitarbeiter',
        state: { filterId: e._id }
      }))
    });

    arr.sort((a, b) => a.priority - b.priority);
    return arr;
  }, [data]);

  // Effect to handle initial expansion after data is loaded
  useEffect(() => {
    if (!loading && data && initialExpandedLabel && expandedIdx === null) {
      const idx = items.findIndex(i => i.label === initialExpandedLabel);
      if (idx !== -1) setExpandedIdx(idx);
      // Special case for DATEV
      if (initialExpandedLabel === 'DATEV (E/A)') {
        const dIdx = items.findIndex(i => i.label.includes('DATEV'));
        if (dIdx !== -1) setExpandedIdx(dIdx);
      }
    }
  }, [loading, data, initialExpandedLabel, items, expandedIdx]);

  const handleNav = (route, state = null) => {
    onClose();
    navigate(route, { state });
  };

  const totalCount = items.reduce((sum, i) => sum + (i.count || 0), 0);

  const toggleExpand = (idx, e) => {
    e.stopPropagation();
    setExpandedIdx(expandedIdx === idx ? null : idx);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#f8fafc',
          backgroundImage: 'none',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: isMobile ? 0 : 6,
          maxHeight: isMobile ? '100%' : '85vh'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 3, pb: 2,
        bgcolor: '#fff',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography sx={{ color: '#0f172a', fontWeight: 900, fontSize: 24, lineHeight: 1.2 }}>
              Meldungen
            </Typography>
            {items.length > 0 && (
              <Typography sx={{ color: '#94a3b8', fontWeight: 600, fontSize: 13, mt: 0.5 }}>
                {totalCount} offene Punkte · {items.length} Kategorie{items.length > 1 ? 'n' : ''}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} sx={{
            color: '#64748b', bgcolor: '#f1f5f9',
            width: 40, height: 40,
            '&:hover': { bgcolor: '#e2e8f0' }
          }}>
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2, pb: 12 }}>
        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress size={32} sx={{ color: '#94a3b8', mb: 2 }} />
            <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>Analysiere Daten...</Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ py: 12, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 64, mb: 2 }}>🚀</Typography>
            <Typography sx={{ color: '#0f172a', fontWeight: 900, fontSize: 20 }}>Alles unter Kontrolle!</Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: 14, mt: 1 }}>Keine kritischen Meldungen vorhanden</Typography>
          </Box>
        ) : (
          <Stack gap={2}>
            {items.map((item, idx) => {
              const isExpanded = expandedIdx === idx;
              const hasDetailItems = item.detailItems && item.detailItems.length > 0;

              return (
                <Box key={idx} sx={{ borderRadius: 5, overflow: 'hidden', transition: 'all 0.2s' }}>
                  {/* Category Header Card */}
                  <Box
                    onClick={(e) => hasDetailItems ? toggleExpand(idx, e) : handleNav(item.route, item.state)}
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: isExpanded ? '20px 20px 0 0' : 5,
                      border: `1px solid ${isExpanded ? item.border : '#e2e8f0'}`,
                      borderBottom: isExpanded ? `1px solid ${item.border}` : `1px solid #e2e8f0`,
                      p: 2.5,
                      display: 'flex', alignItems: 'center', gap: 2,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': { bgcolor: '#fcfdfe', borderColor: item.color },
                      '&:active': { transform: 'scale(0.995)' }
                    }}
                  >
                    <Avatar sx={{
                      bgcolor: item.bg, color: item.color,
                      width: 48, height: 48, borderRadius: 3,
                      border: `1px solid ${item.border}`,
                      flexShrink: 0
                    }}>
                      {React.cloneElement(item.icon, { sx: { fontSize: 24 } })}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: '#1e293b', fontWeight: 800, fontSize: 15, lineHeight: 1.3 }}>
                        {item.label}
                      </Typography>
                    </Box>

                    {/* Count Badge */}
                    <Box sx={{
                      bgcolor: item.bg, color: item.color,
                      px: 1.5, py: 0.5, borderRadius: '14px',
                      fontSize: 15, fontWeight: 900,
                      border: `1px solid ${item.border}`,
                      minWidth: 40, textAlign: 'center', flexShrink: 0
                    }}>
                      {item.count}
                    </Box>

                    {/* Expand icon */}
                    {hasDetailItems && (
                      <IconButton
                        size="small"
                        onClick={(e) => toggleExpand(idx, e)}
                        sx={{ color: '#94a3b8', ml: -0.5, flexShrink: 0, '&:hover': { color: '#64748b', bgcolor: '#f1f5f9' } }}
                      >
                        {isExpanded ? <ExpandLessIcon sx={{ fontSize: 24 }} /> : <ExpandMoreIcon sx={{ fontSize: 24 }} />}
                      </IconButton>
                    )}
                  </Box>

                  {/* Expandable Detail Grid (Kacheln) */}
                  {hasDetailItems && (
                    <Collapse in={isExpanded}>
                      <Box sx={{
                        bgcolor: '#fff',
                        borderRadius: '0 0 20px 20px',
                        border: `1px solid ${item.border}`,
                        borderTop: 'none',
                        p: 2.5,
                        maxHeight: 500,
                        overflowY: 'auto',
                      }}>
                        <Grid container spacing={1.5}>
                          {item.detailItems.map((detail, dIdx) => (
                            <Grid item xs={12} sm={6} md={4} key={dIdx}>
                              <Box
                                onClick={() => handleNav(detail.route, detail.state)}
                                sx={{
                                  bgcolor: '#f8fafc',
                                  borderRadius: 4,
                                  border: '1px solid #e2e8f0',
                                  p: 2,
                                  height: '100%',
                                  display: 'flex', flexDirection: 'column', gap: 0.5,
                                  cursor: 'pointer',
                                  transition: 'all 0.12s',
                                  '&:hover': {
                                    borderColor: item.color,
                                    bgcolor: '#fff',
                                    boxShadow: `0 4px 12px ${item.color}15`,
                                    transform: 'translateY(-2px)'
                                  },
                                  '&:active': { transform: 'scale(0.97)' }
                                }}
                              >
                                <Typography sx={{ color: '#0f172a', fontWeight: 800, fontSize: 13, lineHeight: 1.3 }}>
                                  {detail.title}
                                </Typography>
                                <Typography sx={{ color: '#64748b', fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>
                                  {detail.subtitle}
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                <Stack direction="row" justifyContent="flex-end">
                                  <ChevronRight sx={{ color: '#cbd5e1', fontSize: 18 }} />
                                </Stack>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Dialog>
  );
};

export default NotificationPopup;
